import type { CreateOnKeyDown } from '../../types';
import type { GeneralKeyboardControlsPluginPropertyOptions } from './types';
import { createEditorWarning, navigateForward, navigateBack } from '../../utils';
import { TEXT_SOURCE_STYLING_PLUGIN_ID } from '../textSourceStyling/types';
import isHotkey from 'is-hotkey';
import { logger } from 'modules/logger';
import {
  measureReportNavigationEnd,
  measureReportNavigationStart,
  ReportNavigationDirection,
  ReportNavigationMethod,
} from '../../analytics/performance';
import { slateSource } from '../../core/slateSource';
import { DICTATION_ID_TEXT_MARK, DICTATION_INDEX_TEXT_MARK } from '../dictation';

export const onKeyDownGeneralKeyboardControls: CreateOnKeyDown<
  GeneralKeyboardControlsPluginPropertyOptions
> =
  ({ pluginID, onKeyDownOverride, ignoreMergeFieldsInNavigation }) =>
  (...args) => {
    const [e, { editor }] = args;

    const overrideReturnValue = onKeyDownOverride(...args);

    if (overrideReturnValue === true) {
      if (e.defaultPrevented === false) {
        createEditorWarning(
          'You handled an event without preventing default. Slate will handle this event unless you call event.preventDefault(). Calling this under the hood for you. This needs to be fixed at the callsite!'
        );

        e.preventDefault();
      }

      return true;
    }

    if (isHotkey('tab', e) && editor.selection) {
      measureReportNavigationStart();
      e.preventDefault();
      navigateForward({ editor, fallbackPoint: 'end', ignoreMergeFieldsInNavigation });
      measureReportNavigationEnd({
        direction: ReportNavigationDirection.Forward,
        method: ReportNavigationMethod.KeyboardNavigation,
      });

      return true;
    }

    if (isHotkey('shift+tab', e) && editor.selection) {
      e.preventDefault();
      measureReportNavigationStart();
      navigateBack({ editor, ignoreMergeFieldsInNavigation });
      measureReportNavigationEnd({
        direction: ReportNavigationDirection.Back,
        method: ReportNavigationMethod.KeyboardNavigation,
      });

      return true;
    }

    const isTypingKey = (event: KeyboardEvent) => {
      const ignoredKeys = [
        'Control',
        'Alt',
        'Meta',
        'Shift',
        'Tab',
        'ArrowUp',
        'ArrowDown',
        'ArrowLeft',
        'ArrowRight',
        'CapsLock',
        'Escape',
        'PageUp',
        'PageDown',
        'Home',
        'End',
      ];

      return !ignoredKeys.includes(event.key) && !event.ctrlKey && !event.altKey && !event.metaKey;
    };

    // Add the appropriate text source mark for any user-originating typed input.
    if (e.key.length === 1 && isTypingKey(e)) {
      try {
        editor.addMark(TEXT_SOURCE_STYLING_PLUGIN_ID, slateSource.user.keyboard);

        // This mark will have been added if the surrounding text carries it.
        editor.removeMark(DICTATION_ID_TEXT_MARK);
        editor.removeMark(DICTATION_INDEX_TEXT_MARK);
      } catch (error: any) {
        logger.error(
          '[onKeyDownGeneralKeyboardControls] Error occurred adding text source styling mark',
          error
        );
      }
    }
  };
